import arPortal from '../locales/ar/portal.json';
import enPortal from '../locales/en/portal.json';
import { LOCALES, LOCALE_DIRECTION, type Locale } from '@/lib/locale';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import arAuth from '../locales/ar/auth.json';
import arCommon from '../locales/ar/common.json';
import arErrors from '../locales/ar/errors.json';
import arLayout from '../locales/ar/layout.json';
import arValidation from '../locales/ar/validation.json';
import enAuth from '../locales/en/auth.json';
import enCommon from '../locales/en/common.json';
import enErrors from '../locales/en/errors.json';
import enLayout from '../locales/en/layout.json';
import enValidation from '../locales/en/validation.json';

export const DEFAULT_LOCALE: Locale = 'ar';
export const STORAGE_KEY = 'app_language';

/**
 * Arabic is the product's default language, not a translation of an English
 * original: `ar` is both the fallback and the initial value, so a missing key
 * degrades to Arabic rather than to English.
 */
export const resources = {
  ar: {
    common: arCommon,
    portal: arPortal,
    auth: arAuth,
    errors: arErrors,
    layout: arLayout,
    validation: arValidation,
  },
  en: {
    common: enCommon,
    portal: enPortal,
    auth: enAuth,
    errors: enErrors,
    layout: enLayout,
    validation: enValidation,
  },
} as const;

export const NAMESPACES = ['common', 'auth', 'errors', 'layout', 'validation', 'portal'] as const;

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...LOCALES],
    ns: [...NAMESPACES],
    defaultNS: 'common',
    detection: {
      order: ['localStorage', 'htmlTag', 'navigator'],
      lookupLocalStorage: STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: {
      // React already escapes rendered values; double-escaping mangles Arabic
      // punctuation and quotes.
      escapeValue: false,
    },
    returnNull: false,
  });

/**
 * Applies the locale to the document. Called on boot and on every switch so
 * `dir`, `lang` and the stored preference can never disagree.
 */
export function applyLocale(locale: Locale): void {
  const root = document.documentElement;
  const fontFamily = locale === 'ar' ? 'Cairo, system-ui, sans-serif' : 'Inter, sans-serif';
  root.lang = locale;
  root.dir = LOCALE_DIRECTION[locale];
  root.setAttribute('data-locale', locale);
  root.style.fontFamily = fontFamily;

  if (document.body) {
    document.body.style.fontFamily = fontFamily;
  }
}

export async function changeLocale(locale: Locale): Promise<void> {
  await i18n.changeLanguage(locale);
  applyLocale(locale);
}

export function currentLocale(): Locale {
  const language = i18n.resolvedLanguage ?? i18n.language ?? DEFAULT_LOCALE;
  return (LOCALES as readonly string[]).includes(language) ? (language as Locale) : DEFAULT_LOCALE;
}

applyLocale(currentLocale());

export default i18n;
